/**
 * AI Status Badge Component
 *
 * Displays the AI availability status as a small badge.
 * Issue #146: Add UI indicator for AI availability status
 */

import { useEffect, useState } from 'react';
import { useAIStatusStore } from '../../stores/aiStatusStore';

export function AIStatusBadge() {
  const { status, isLoading, fetchStatus, startPolling, onTransition } =
    useAIStatusStore();
  const [showTooltip, setShowTooltip] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    fetchStatus();
    // Issue #824: start polling so mode transitions are detected
    // within 60s and the badge flips automatically.
    startPolling();
    // Cleanup: stop polling when the badge permanently unmounts
    // (e.g., navigation to login page) to prevent interval leaks.
    return () => {
      useAIStatusStore.getState().stopPolling();
    };
  }, [fetchStatus, startPolling]);

  // Issue #824: show a toast when the mode transitions.
  useEffect(() => {
    const unsub = onTransition((newMode, s) => {
      const msg =
        newMode === 'ai-enhanced'
          ? 'AI connection restored — AI Enhanced Mode active.'
          : `AI connection lost — switched to Basic Mode. ${s.lastError ?? ''}`;
      setToast(msg);
      setTimeout(() => setToast(null), 6000);
    });
    return unsub;
  }, [onTransition]);

  if (isLoading && !status) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <span className="w-2 h-2 rounded-full bg-muted-foreground animate-pulse" />
        <span className="hidden sm:inline">AI</span>
      </span>
    );
  }

  if (!status) {
    return null;
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium cursor-help transition-colors ${
          status.available
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
        }`}
      >
        <span
          className={`w-2 h-2 rounded-full ${
            status.available ? 'bg-green-500' : 'bg-yellow-500'
          }`}
        />
        <span className="hidden sm:inline">
          {status.available ? 'AI Enhanced' : 'Basic Mode'}
        </span>
        <span className="sm:hidden">
          {status.available ? 'AI' : 'Basic'}
        </span>
      </span>

      {showTooltip && (
        <div className="absolute z-50 top-full left-1/2 -translate-x-1/2 mt-2 w-64 p-3 bg-popover border border-border rounded-lg shadow-lg text-sm">
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-popover border-l border-t border-border rotate-45" />
          <div className="relative">
            <p className="font-medium mb-1">
              {status.available ? 'AI-Powered Analysis' : 'Basic Analysis Mode'}
            </p>
            <p className="text-muted-foreground text-xs mb-2">
              {status.message}
            </p>
            {status.available && (
              <div className="space-y-1 text-xs text-muted-foreground">
                <p>Provider: {status.provider}</p>
                <p>Model: {status.model}</p>
                {status.cacheEnabled && <p>Cache: Enabled</p>}
                {status.healthy != null && (
                  <p>
                    Health:{' '}
                    <span
                      className={
                        status.healthy
                          ? 'text-green-600'
                          : 'text-red-600'
                      }
                    >
                      {status.healthy ? 'connected' : 'failed'}
                    </span>
                  </p>
                )}
                {status.lastCheckedAt && (
                  <p>
                    Last checked:{' '}
                    {new Date(status.lastCheckedAt).toLocaleTimeString()}
                  </p>
                )}
                {status.latencyMs != null && status.healthy && (
                  <p>Latency: {status.latencyMs}ms</p>
                )}
              </div>
            )}
            {!status.available && (
              <div className="text-xs text-muted-foreground mt-2 space-y-1">
                <p>
                  {status.lastError ?? 'Configure an API key in Settings to enable AI features.'}
                </p>
                {status.lastCheckedAt && (
                  <p>
                    Last checked:{' '}
                    {new Date(status.lastCheckedAt).toLocaleTimeString()}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Issue #824: mode-transition toast */}
      {toast && (
        <div
          role="alert"
          className="fixed bottom-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg border border-border bg-popover text-sm max-w-sm transition-opacity duration-300"
        >
          {toast}
        </div>
      )}
    </div>
  );
}
